import React, { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Breadcrumb } from '../../common/atoms';
import { Pagination } from '../../common/molecules';
import { EmptyState, SkeletonCard } from '../../tailwind/atoms';
import { successOrFailureToaster } from '../../common/atoms/messages';
import { utilizationApi } from '../../../services';
import { handleAppError } from '../../handlers';
import { SALES_ROLE } from '../../../constants/overview';
import { dsButtonPrimary, joinButtonClasses } from '../../../utils/buttonStyles';
import { download_btn, filter_plus, filter_plus_blue } from '../../../assets/images';
import ActivityLogFiltersModal from '../../modals/ActivityLogFiltersModal';
const COLUMNS = [
  ['date', 'Date'], ['employee', 'Employee'], ['employee_type', 'Type'], ['department', 'Department'], ['site', 'Site'],
  ['visit_type', 'Visit type'], ['health_issue', 'Health issue'], ['flags', 'Incident'], ['doctor', 'Doctor'], ['status', 'Status'],
];

const incident = (row) => [row.injury && 'Injury', row.emergency && (row.admitted ? 'Emergency, admitted' : 'Emergency')].filter(Boolean).join(' · ') || '—';

/**
 * Utilization › Occupational Health Centre › Activity log: every OHC visit in the period, 10 a page, with who
 * came and what kind of visit it was. No clinical detail (complaint, diagnosis, medicines) is shown or
 * downloaded. Filters work as on the other activity logs (the shared filter panel, with OHC's own site,
 * visit type, health issue and employee type lists); Download data emails the filtered list as Excel.
 */
const OhcActivityLog = ({ location, history }) => {
  const period = location.state || {};
  // Applied filters and dates, and the ones being picked in the open panel
  const [filters, setFilters] = useState({});
  const [dates, setDates] = useState({ startDate: period.startDate, endDate: period.endDate });
  const [filterParams, setFilterParamsState] = useState({});
  const [filterDates, setFilterDates] = useState({ startDate: period.startDate, endDate: period.endDate });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  // A failed fetch keeps the last good rows' filter lists and says so, rather than claiming there are no visits
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!period.contractId) history.replace('/utilization/ohc');
  }, [period.contractId, history]);

  useEffect(() => {
    if (!period.contractId) return undefined;
    const source = axios.CancelToken.source();
    setLoading(true);
    setFailed(false);
    utilizationApi.ohcActivityLog({ ...period, ...dates, page: page + 1, filters, cancelToken: source.token })
      .then((response) => { setData(response.data); setLoading(false); })
      .catch((error) => { if (!axios.isCancel(error)) { setFailed(true); setLoading(false); handleAppError(error); } });
    return () => source.cancel();
  }, [period.contractId, dates, page, filters, attempt]);

  const options = data?.filter_options || {};
  const count = Object.keys(filters).length;

  // The shared panel's callbacks, as the consultation log handles them
  const setFilterParams = (value, type) => {
    if (type === 'clear') return setFilterParamsState({});
    if (type === 'age_from' || type === 'age_to') {
      return setFilterParamsState((prev) => ({ ...prev, age: { ...prev.age, [type === 'age_from' ? 'from' : 'to']: value } }));
    }
    return setFilterParamsState((prev) => ({ ...prev, [type]: value }));
  };
  const clearFilters = (type) => setFilterParamsState(({ [type]: _removed, ...rest }) => rest);
  const changeDates = (e, type) => {
    if (type === 'reset' || !e) {
      setFilterDates((prev) => (type === 'reset' ? { startDate: period.startDate, endDate: period.endDate }
        : { ...prev, [type]: type === 'startDate' ? period.startDate : period.endDate }));
      return;
    }
    setFilterDates((prev) => ({ ...prev, [type]: moment(e).format('YYYY-MM-DD') }));
  };
  const applyFilter = () => {
    setShowFilters(false);
    setPage(0);
    setFilters(filterParams);
    setDates(filterDates);
  };
  const cancelFilters = () => {
    setShowFilters(false);
    setFilterParamsState({});
    setFilterDates({ startDate: period.startDate, endDate: period.endDate });
    setFilters({});
    setDates({ startDate: period.startDate, endDate: period.endDate });
    setPage(0);
  };

  const download = () => {
    setDownloading(true);
    utilizationApi.ohcActivityLogDownload({ ...period, ...dates, filters })
      .then(() => successOrFailureToaster({ message: "We're preparing the Excel. It will be emailed to you shortly.", type: 'success' }))
      .catch(() => successOrFailureToaster({ message: 'Could not start the download. Please try again.', type: 'fail' }))
      .finally(() => setDownloading(false));
  };

  return (
    <div className='ds-report -mx-4 -my-6 min-h-full bg-gray-100 px-ds-6 pb-16 pt-ds-8 sm:-mx-6 sm:-my-8 lg:px-12 lg:pb-24 lg:pt-11'>
      <div className='mx-auto max-w-[1240px]'>
        <div className='mb-ds-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between'>
          <div>
            <Breadcrumb list={[{ text: 'Utilization', link: '/utilization/ahc' }, { text: 'Occupational Health Centre', link: '/utilization/ohc' },
              { text: 'Activity log', link: '#' }]} />
            <h1 className='m-0 mt-ds-3 font-fig-extrabold text-[34px] font-bold text-gray-900'>OHC activity log</h1>
            <p className='m-0 mt-1 text-[13px] text-gray-600'>
              Every OHC visit from {dates.startDate} to {dates.endDate}. Complaints, diagnoses and medicines stay with the doctor and are not shown.
            </p>
          </div>
        </div>

        <div className='relative mb-ds-4 flex justify-end gap-4'>
          <button type='button' onClick={() => setShowFilters(!showFilters)} disabled={!data}
            className='flex h-9 items-center rounded border bg-white py-2 pl-2 pr-3 text-lg font-semibold'>
            <img src={count > 0 ? filter_plus_blue : filter_plus} className='mr-2' style={{ width: 18 }} alt='filters' />
            <span className={`mr-1 text-lg font-semibold ${count > 0 ? 'text-blue-500' : 'text-gray-600'}`}>Filter</span>
            {count > 0 ? (
              <span className='flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-white' style={{ fontSize: '0.6rem' }}>{count}</span>
            ) : null}
          </button>
          {showFilters ? (
            <ActivityLogFiltersModal
              benefit_type='ohc'
              visible={showFilters}
              filterParams={filterParams}
              startDate={period.startDate}
              endDate={period.endDate}
              filterDates={filterDates}
              changeDates={changeDates}
              setFilterParams={setFilterParams}
              clearFilters={clearFilters}
              onCancel={cancelFilters}
              applyFilter={applyFilter}
              count={Object.keys(filterParams).length + (filterDates.startDate !== period.startDate || filterDates.endDate !== period.endDate ? 1 : 0)}
              ohcOptions={options}
            />
          ) : null}
          <button type='button' onClick={download} disabled={loading || failed || downloading || !data?.total || SALES_ROLE === 'sales'}
            className={joinButtonClasses(dsButtonPrimary, 'h-9 w-40 px-2 whitespace-nowrap', (failed || !data?.total || SALES_ROLE === 'sales') && 'cursor-not-allowed')}>
            <img src={download_btn} className='mr-2' style={{ width: 14 }} alt='' />
            {downloading ? 'Starting…' : 'Download data'}
          </button>
        </div>

        {loading ? <SkeletonCard className='h-[420px] bg-white' /> : null}
        {!loading && failed ? (
          <EmptyState title='Could not load the activity log' description='Something went wrong while fetching the visits.'
            action={<button type='button' onClick={() => setAttempt((n) => n + 1)} className={joinButtonClasses(dsButtonPrimary, 'h-9 px-5')}>Retry</button>} />
        ) : null}
        {!loading && !failed && data.total === 0 ? <EmptyState title='No OHC visits' description='No visits match these filters in this period.' /> : null}
        {!loading && !failed && data.total > 0 ? (
          <div className='overflow-x-auto rounded-ds-lg border border-gray-300 bg-white'>
            <table className='w-full border-collapse text-[13px]'>
              <thead>
                <tr className='border-b border-gray-200 text-left text-ts-2xs uppercase tracking-[0.08em] text-gray-500'>
                  {COLUMNS.map(([key, label]) => <th key={key} className='whitespace-nowrap px-ds-4 py-ds-3 font-semibold'>{label}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.visit_id} className='border-b border-gray-100 align-top last:border-0'>
                    <td className='whitespace-nowrap px-ds-4 py-ds-3 font-jetbrains-mono text-[12px]'>{row.date}<div className='text-gray-500'>{row.time}</div></td>
                    <td className='px-ds-4 py-ds-3'>
                      <div className='font-semibold text-gray-900'>{row.name}</div>
                      <div className='font-jetbrains-mono text-[12px] text-gray-600'>{row.employee_id || '—'}</div>
                      {row.email ? <div className='text-[12px] text-gray-500'>{row.email}</div> : null}
                    </td>
                    <td className='whitespace-nowrap px-ds-4 py-ds-3'>{row.employee_type}</td>
                    <td className='px-ds-4 py-ds-3'>{row.department || '—'}</td>
                    <td className='whitespace-nowrap px-ds-4 py-ds-3'>{row.site}</td>
                    <td className='whitespace-nowrap px-ds-4 py-ds-3'>{row.visit_type || '—'}</td>
                    <td className='whitespace-nowrap px-ds-4 py-ds-3'>{row.health_issue}</td>
                    <td className='whitespace-nowrap px-ds-4 py-ds-3'>{incident(row)}</td>
                    <td className='whitespace-nowrap px-ds-4 py-ds-3'>{row.doctor}</td>
                    <td className='whitespace-nowrap px-ds-4 py-ds-3'>{row.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {!loading && data?.total > 0 ? (
          <Pagination curpage={page} totalpages={Math.ceil(data.total / (data.per_page || 10))} totalresults={data.total} handler={setPage} />
        ) : null}
      </div>
    </div>
  );
};

export default OhcActivityLog;
